<template>
    <div class="wrapper" ref="wrapper">
        <div 
            :class="marqueeClassName" 
            ref="marquee" 
            :style="{
                '--overflow-width': overflowWidth, 
                '--animation-duration': animationDuration, 
                '--padding': padding,
                '--divider-padding': dividerPadding
            }"
        > 
            <span v-for="(item, index) in items" :key="index">
                {{item}}
                <span class="divider" v-if="index < items.length - 1">{{ divider }}</span>
            </span>
        </div>
    </div>
</template>

<script>
export default {
    name: "Marquee",

    props: {
        /**
         * An array of strings to be displayed in the marquee.
         * Strings are concatenated using a divider.
         */
        items: {
            required: true,
            type: Array,
        },

        /**
         * The padding used at the start and end of the text. 
         * Must include a unit to prevent errors in CSS calc function!
         */
        padding: {
            type: String,
            default: '2vw',
            validator: function (value) {
                return value.match(/[0-9.]+[a-z%]+/)
            }
        },

        /**
         * Total length of the margue animation. 
         * If not passed, animation duration and animation delays are computed automatically.
         */
        fixedAnimationDuration: {
            type: Number
        },

        /**
         * The divider character used in between the individual items when concatenating them.
         */
        divider: {
            type: String,
            default: '|',
        },

        /**
         * The horizontal spacing around the divider.
         */
        dividerPadding: {
            type: String,
            default: '0.75vw',
        },
    },

    data() {
        return {
            /** 
             * An interval controlling the repetition of the animation.
             */
            interval: null,

            /**
             * This stores the real width in pixels of the wrapper element (marquee's parent).
             */
            wrapperWidth: 0,

            /**
             * This stores the real width in pixels of the text displayed in the marquee.
             */
            textWidth: 0,

            /**
             * Controls the animation state.
             */
            isRunning: false,
        };
    },

    watch: {
        /**
         * After changing the displayed texts, recalculate the widths required for the animation.
         * It is done in the next tick to allow the new text to be rendered. Otherwise, it is not possible to measure the new widths.
         */
        items() {
            this.$nextTick(() => {
                this.measureWidths();
                this.startAnimation();
            });
        },
    },

    /**
     * After the text has been rendered (hence, in mounted), measure its widths and start animating if necessary.
     */
    mounted() {
        this.measureWidths();
        this.startAnimation();
    },

    computed: {
        /**
         * How much pixels of the text is hidden behind the elements and must be scrolled.
         * This number is positive or 0 if the whole text fits into the available container.
         */
        overflowWidth() {
            return this.textWidth - this.wrapperWidth;
        },

        /**
         * Returns animation duration in milliseconds without delays.
         * It is based on how much of the text is hidden and hence must be scrolled.
         */
        animationDuration() {
            const divider = this.fixedAnimationDuration ? Math.round(this.fixedAnimationDuration / 10000) : 1;
            if (this.fixedAnimationDuration) {
                return this.fixedAnimationDuration/divider - 2*this.animationDelay;
            } else {
                return this.overflowWidth * 10;
            }
        },

        /**
         * A small delay for pausing the animation at its start and end to avoid quick repetitions.
         * This gives some time for the user to read the text at the start and end before it is moved away.
         * In milliseconds.
         */
        animationDelay() {
            return (this.fixedAnimationDuration && this.fixedAnimationDuration < 3000) 
                ? this.fixedAnimationDuration*0.25
                : 1000;
        },

        /**
         * The total animation duration including one delay at the start and another at the end.
         * In milliseconds.
         */
        animationDurationWithDelays() {
            return this.animationDuration + 2 * this.animationDelay;
        },

        /**
         * Generates the CSS class for the marquee element.
         * Dynamically controls whether the CSS animation is running.
         */
        marqueeClassName() {
            let className = 'marquee ';
            if (this.isRunning) className += ' running ';
            return className;
        },
    },

    methods: {
        /**
         * Retrieve the real widths of rendered elements.
         */
        measureWidths() {
            this.wrapperWidth = this.$refs.wrapper.offsetWidth;
            this.textWidth = this.$refs.marquee.scrollWidth;
        },

        /**
         * Starts the repeated animation.
         * If the overflowed width is <=0, the animation is not started because the whole text fits on the screen.
         */
        startAnimation() {
            this.stopAnimation();
            if (this.overflowWidth > 0) {
                this.animate();
                this.interval = setInterval(() => this.animate(), this.animationDurationWithDelays);
            }
        },

        /**
         * Stops the animation.
         */
        stopAnimation() {
            if (this.interval !== null) {
                clearInterval(this.interval);
                this.interval = null;
            }
        },

        /**
         * One animation sequence.
         * Includes a small delay for pausing the animation at its start to avoid quick repetitions.
         * The delay gives some time for the user to read the text at the start before it is moved away.
         */
        animate() {
            // 1. reset the animation to its beginning by removing the class - the text start is at the left side of the container
            this.isRunning = false;
            // 2. After a delay, start moving the text towards to the left
            setTimeout(() => this.isRunning = true, this.animationDelay);
        },
    }
}
</script>

<style scoped>

    /* CSS marquee based on: https://tympanus.net/codrops/2020/03/31/css-only-marquee-effect/ */

    /**
    The wrapper always takes 100% of the parent's width.
    The parent must be `position: relative` or `position:absolute` to have the width of the marquee equal to its parent.
     */
    .wrapper {
        width: 100%;
        overflow: hidden;
    }

    /** 
    Prepare the main element, which is being moved horizontally using a CSS animation.
    CSS variables are used to control the animation's duration and animated distance.
     */
    .marquee {
        /* 
        The starting point of the animation is when the start of the first item is at the left side of the container.
        */
        --move-initial: 0px; 

        /**
        Finish the animation at such position that the end of the last item is at the right side of the container.
        The overflow width is inversed because the animation moves toward left (negative axes).
        Moreover, some horizontal padding is added at the end of the text.
         */
        --move-final: calc(-1px * var(--overflow-width) - var(--padding)); 

        position: relative;

        padding-left: var(--padding);
        padding-right: var(--padding);

        overflow: visible; /* Let the text overflow behind the container element's width. */
        white-space: nowrap; /* We do not want any new lines. */

        transform: translate3d(var(--move-initial), 0, 0); /* The initial text position. */
        animation-duration: calc(var(--animation-duration) * 1ms); /* The duration is converted to milliseconds. */
        animation-timing-function: linear;
        animation-fill-mode: forwards; /* Do not reset the text position at the animation's end. Keep showing the end of the text. */
        animation-name: none; /* The animation is stopped by default. Use .running to enable it. */
        animation-play-state: paused;  /* The animation is stopped by default. Use .running to enable it. */
    }

    /**
    Add this class to the element to start the animation from its beggining. Remove it to stop the animation.
    The animation name is set here to start the animation always from the beginning (the text on the left).
    If the name was set in .marquee, the animation would continue where it paused. We do not want such behaviour.
    */
    .marquee.running {
        animation-name: marquee;
        animation-play-state: running;
    }

    /**
    The marquee is animated from the initial position to the final position using a translation on the horizontal axis.
    */
    @keyframes marquee {
        from {
            transform: translate3d(var(--move-initial), 0, 0);
        }
        to {
            transform: translate3d(var(--move-final), 0, 0);
        }
    }

    .marquee span {
        display: inline-block;
        width: fit-content;
    }

    .divider {
        padding-left: var(--divider-padding);
        padding-right: var(--divider-padding);
        display: inline-block;
    }
</style>